'use client';

// The Termos de Uso / Política de Privacidade line under the login card.
//
// Why it exists at all: terms a visitor cannot read before signing up are not
// terms they can agree to. Everything else in this product sits behind the
// gate; these two documents are the only `/api` reads the daemon answers
// without a credential (`isPublicLegalReadPath` in `routes/legal.ts`), and
// this is the surface that needs them.
//
// The copy is pt-BR literals, matching `LoginView`'s own documented choice —
// this whole surface is the hosted-Brazil product and does not go through the
// i18n Dict. `renderMarkdown` does, but `useI18n` falls back to a stand-alone
// English translator when no provider is mounted, and the only string it would
// reach for is a code-block copy button that a legal document never has.
//
// Downloads are plain anchors, unlike Settings → Termos, which fetches and
// hands out a blob. There the caller holds a session and the blob path keeps
// working even if the public allowlist is ever closed; here there is no
// session by construction, so the anchor is the whole mechanism.

import { useCallback, useEffect, useState } from 'react';

import { legalExportFileName, type LegalDocument, type LegalDocumentId } from '@open-design/contracts';

import { renderMarkdown } from '../runtime/markdown';
import styles from './LegalNotice.module.css';

const LABELS: Record<LegalDocumentId, string> = {
  terms: 'Termos de Uso',
  privacy: 'Política de Privacidade',
};

function LegalDialog({ id, onClose }: { id: LegalDocumentId; onClose: () => void }) {
  const [document_, setDocument] = useState<LegalDocument | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(`/api/legal/${id}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const payload = (await res.json()) as { document: LegalDocument };
        if (!cancelled) setDocument(payload.document);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Escape closes, like every other dismissible layer in the product.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className={styles.backdrop}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label={LABELS[id]}>
        <header className={styles.dialogHead}>
          <h2>{document_?.title ?? LABELS[id]}</h2>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <div className={styles.body}>
          {failed ? (
            <p className={styles.failed}>
              Não foi possível carregar o documento. Tente novamente em instantes.
            </p>
          ) : document_ ? (
            renderMarkdown(document_.body)
          ) : (
            <p className={styles.loading}>Carregando…</p>
          )}
        </div>

        <footer className={styles.dialogFoot}>
          <a
            className={styles.download}
            href={`/api/legal/${id}/export?format=pdf`}
            download={legalExportFileName(id, 'pdf')}
          >
            Baixar PDF
          </a>
          <a
            className={styles.download}
            href={`/api/legal/${id}/export?format=docx`}
            download={legalExportFileName(id, 'docx')}
          >
            Baixar DOCX
          </a>
        </footer>
      </div>
    </div>
  );
}

/**
 * The consent line. Phrased as agreement rather than as two bare links,
 * because that is what the sentence is doing legally at a signup screen.
 */
export function LegalNotice() {
  const [open, setOpen] = useState<LegalDocumentId | null>(null);
  const close = useCallback(() => setOpen(null), []);

  return (
    <>
      <p className={styles.notice}>
        Ao criar uma conta, você concorda com os{' '}
        <button type="button" className={styles.link} onClick={() => setOpen('terms')}>
          Termos de Uso
        </button>{' '}
        e a{' '}
        <button type="button" className={styles.link} onClick={() => setOpen('privacy')}>
          Política de Privacidade
        </button>
        .
      </p>
      {open ? <LegalDialog id={open} onClose={close} /> : null}
    </>
  );
}
